import {
    Button,
    Checkbox,
    DatePicker,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Select,
    Space,
} from "antd";
import type { Project, PurchaseGoods, PurchaseGoodsReceiptVo } from "@zhihaoscm/entity";
import {
    PERMISSION,
    PROJECT_STATE,
    PURCHASE_GOODS_RECEIPT_STATE,
    PURCHASE_PAYMENT_TYPE,
    PURCHASE_TYPE,
    ROLE_CODE
} from "@zhihaoscm/meta";
import { dateFormat, dateTimeFormat, formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectService } from "@/api/projectService";
import { purchaseGoodsService } from "@/api/purchaseGoodsService";
import { purchaseGoodsReceiptService } from "@/api/purchaseGoodsReceiptService";
import {
    Breadcrumb,
    DetailButtons,
    DetailHeader,
    InfoCard,
    ProTable,
    ZhBaseItem,
    ZhGrid,
    ZhInsetTabs,
    ZhMessageBox,
    type DetailAction,
    type ProColumn
} from "@/components/common";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import { PAYMENT_QUERY_TYPE } from "../../../../../common/enum/purchasePayment";
import {
    DOWN_STREAM_PURCHASE_GOODS_RECEIPT_STATE,
    FORWARD,
    RECIEPT_RECONCILIATION_STATE,
    SIGN_STATE
} from "../../../../../common/enum/purchaseGoodsReceipt";
import type { PurchaseGoodsReceiptForm } from "../../../../../common/types/purchaseGoodsReceipt";
import "./console.css";

const options = (meta: { options: Array<{ label: string; value: string | number }> }) =>
    meta.options.map(item => ({ label: item.label, value: Number(item.value) }));

type ReceiptActive = "Up" | "Down";
/** 签收列表逐项对应旧 index.vue 的角色页签、查询类型、字段和操作。 */
export function ReceiptListPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useUserSession();
    const account = useAccountSession();
    const roleCode = user.roleCode ?? ROLE_CODE.CUSTOMER.code;
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [active, setActive] = useState<ReceiptActive>(() => {
        const receiptType = searchParams.get("purGoodsReceiptType");
        return receiptType === "Up" || receiptType === "Down" ? receiptType : isCustomer ? "Up" : "Down";
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejectRow, setRejectRow] = useState<PurchaseGoodsReceiptVo>();
    const [rejectReason, setRejectReason] = useState("");
    const isUp = active === "Up";
    const queryType = isUp ? PAYMENT_QUERY_TYPE.PAY_CUSTOMER.code : PAYMENT_QUERY_TYPE.COLLECT_CUSTOMER.code;
    const title = isCustomer || isSupplier ? "我的签收单" : undefined;
    const states = options(isUp ? DOWN_STREAM_PURCHASE_GOODS_RECEIPT_STATE : PURCHASE_GOODS_RECEIPT_STATE);
    const reconciliationStates = options(RECIEPT_RECONCILIATION_STATE);
    const forwards = options(FORWARD);
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT ||
        Boolean((user.userInfo as any).permissions?.includes(PERMISSION.SIGNATURE.code));
    const reload = () => setRefreshKey(value => value + 1);
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (!data?.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
            return;
        }
        window.open(data.ticket);
        reload();
    };
    const confirm = (tip: string, action: () => Promise<unknown>, title?: string) =>
        setConfirmDialog({ title, tip, action });
    const reject = (row: PurchaseGoodsReceiptVo) => {
        setRejectReason("");
        setRejectRow(row);
    };
    /** 复合签署状态低 16 位与旧 analysisState.querySigned 保持一致。 */
    const signedSelf = (row: PurchaseGoodsReceiptVo, invalid = false) => {
        const state = invalid
            ? row.purchaseGoodsReceipt.invalidSignState
            : Number((row.purchaseGoodsReceipt as any).complexState) & 0xffff;
        if (state === SIGN_STATE.ALL_SIGN.code) return true;
        if (isCustomer) return state === SIGN_STATE.BUYER_SIGN.code;
        if (isSupplier) return state === SIGN_STATE.SELLER_SIGN.code;
        return isUp ? state === SIGN_STATE.BUYER_SIGN.code : state === SIGN_STATE.SELLER_SIGN.code;
    };
    const sealUser = (row: PurchaseGoodsReceiptVo) => {
        const receipt = row.purchaseGoodsReceipt;
        const proxyIsReceiver = String(account.proxyActualAccount?.id) === String(receipt.receiverCustomerId);
        const signId = proxyIsReceiver
            ? (receipt.receiverEnterprise as any)?.signId
            : (receipt.supplierEnterprise as any)?.signId;
        return String(account.actualAccount?.id) === String(signId);
    };
    const operation = (row: PurchaseGoodsReceiptVo) => {
        const receipt = row.purchaseGoodsReceipt;
        const draft = receipt.state === PURCHASE_GOODS_RECEIPT_STATE.DRAFT.code;
        const confirming = receipt.state === PURCHASE_GOODS_RECEIPT_STATE.CONFIRMING.code;
        const invaliding = receipt.state === PURCHASE_GOODS_RECEIPT_STATE.INVALIDING.code;
        const related = row.relatedPurchaseGoodsReceiptList || [];
        const relatedFinished =
            related.length > 0 && related.every(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code);
        const relatedRejectable =
            related.length > 0 &&
            related.every(item =>
                [PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code, PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code].includes(
                    item.state
                )
            ) &&
            related.some(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code);
        const relatedInvalid =
            related.length > 0 && related.every(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.INVALID.code);
        const buttons = [] as ReactNode[];
        if (!isUp) {
            if (isSupplier && hasPermission && (draft || receipt.state === PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code))
                buttons.push(
                    <Button
                        key="edit"
                        type="link"
                        onClick={() => navigate(`/console/purchaseGoodsReceipt/edit?id=${receipt.id}`)}
                    >
                        修改
                    </Button>
                );
            if (draft && hasPermission)
                buttons.push(
                    <Button
                        key="start"
                        type="link"
                        onClick={() => void openTicket(() => purchaseGoodsReceiptService.startSign(receipt.id))}
                    >
                        发起签署
                    </Button>
                );
            if (confirming && !signedSelf(row) && sealUser(row))
                buttons.push(
                    <Button
                        key="sign"
                        type="link"
                        onClick={() => void openTicket(() => purchaseGoodsReceiptService.sign(receipt.id))}
                    >
                        签署
                    </Button>
                );
            if (isSupplier && draft && !related.length)
                buttons.push(
                    <Button
                        key="delete"
                        danger
                        type="link"
                        onClick={() => confirm("是否删除", () => purchaseGoodsReceiptService.delete(receipt.id))}
                    >
                        删除
                    </Button>
                );
            if (
                isSupplier &&
                hasPermission &&
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code &&
                receipt.reconciliationState === RECIEPT_RECONCILIATION_STATE.INITIAL.code
            )
                buttons.push(
                    <Button
                        key="invalid"
                        type="link"
                        onClick={() =>
                            confirm(
                                "作废后，需双方签署作废签收单后才作废，是否发起作废？",
                                () => openTicket(() => purchaseGoodsReceiptService.startInvalid(Number(receipt.id))),
                                "已发起作废"
                            )
                        }
                    >
                        发起作废
                    </Button>
                );
        } else {
            if (((isSupplyChain && relatedFinished) || isCustomer) && confirming && !signedSelf(row) && sealUser(row))
                buttons.push(
                    <Button
                        key="sign"
                        type="link"
                        onClick={() => void openTicket(() => purchaseGoodsReceiptService.sign(receipt.id))}
                    >
                        签署
                    </Button>
                );
            if (((isSupplyChain && relatedRejectable) || isCustomer) && confirming && hasPermission)
                buttons.push(
                    <Button key="reject" type="link" onClick={() => reject(row)}>
                        驳回
                    </Button>
                );
            if (isSupplyChain && receipt.forward === FORWARD.UNFORWARD.code && confirming)
                buttons.push(
                    <Button
                        key="forward"
                        type="link"
                        onClick={() => navigate(`/console/purchaseGoodsReceipt/edit?id=${receipt.id}&forward=1`)}
                    >
                        转发给下游
                    </Button>
                );
        }
        if (invaliding && !signedSelf(row, true) && sealUser(row) && (!(isSupplyChain && isUp) || relatedInvalid))
            buttons.push(
                <Button
                    key="invalid-sign"
                    type="link"
                    onClick={() => void openTicket(() => purchaseGoodsReceiptService.sign(receipt.id))}
                >
                    签署作废文件
                </Button>
            );
        return buttons.length ? <Space className="receipt-operation">{buttons}</Space> : "--";
    };
    const columns = useMemo<ProColumn<PurchaseGoodsReceiptVo>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                width: 150,
                search: { key: "keyword", type: "input", placeholder: "请输入编号或项目名称" },
                render: (_, row) => (
                    <Button
                        className="receipt-code-link"
                        type="link"
                        onClick={() =>
                            navigate(
                                `/console/purchaseGoodsReceipt/detail?id=${row.purchaseGoodsReceipt.id}&purGoodsReceiptType=${active}`
                            )
                        }
                    >
                        {row.purchaseGoodsReceipt.id}
                    </Button>
                )
            },
            { key: "project", title: "项目", minWidth: 170, render: (_, row) => row.purchaseGoodsReceipt.projectName },
            {
                key: "enterprise",
                title: isUp ? "供货单位" : "签收单位",
                minWidth: 114,
                search: {
                    key: "enterprise",
                    type: "input",
                    placeholder: isUp ? "请输入供货单位名" : "请输入签收单位名"
                },
                render: (_, row) => {
                    const enterprise = isUp
                        ? row.purchaseGoodsReceipt.supplierEnterprise
                        : row.purchaseGoodsReceipt.receiverEnterprise;
                    return (
                        enterprise?.name ||
                        (enterprise as any)?.legalRepresentative ||
                        (enterprise as any)?.realName ||
                        "--"
                    );
                }
            },
            { key: "quantity", title: "吨数", minWidth: 114, render: (_, row) => formatNumber(row.purchaseGoodsReceipt.quantity) },
            {
                key: "date",
                title: "确认日期",
                minWidth: 114,
                search: { key: "receiptConfirmedDate", type: "dateRange", placeholder: "确认日期范围选择", width: 80 },
                render: (_, row) => dateFormat(row.purchaseGoodsReceipt.receiptConfirmedDate)
            },
            {
                key: "state",
                title: "签收状态",
                minWidth: 86,
                enum: states,
                search: { key: "state", type: "select", placeholder: "请选择签收状态", options: states }
            },
            {
                key: "reconciliation",
                title: "对账状态",
                minWidth: 86,
                enum: reconciliationStates,
                render: (_, row) =>
                    row.purchaseGoodsReceipt.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code
                        ? RECIEPT_RECONCILIATION_STATE.getIntroByCode(row.purchaseGoodsReceipt.reconciliationState)
                        : "--"
            },
            ...(isUp && isSupplyChain
                ? [
                      {
                          key: "forward",
                          title: "是否已转发",
                          minWidth: 100,
                          enum: forwards,
                          search: {
                              key: "forward",
                              type: "select" as const,
                              placeholder: "请选择是否已转发",
                              options: forwards
                          }
                      }
                  ]
                : []),
            { key: "operation", title: "操作", width: 240, fixed: "right", render: (_, row) => operation(row) }
        ],
        [active, forwards, isSupplyChain, isUp, navigate, reconciliationStates, states]
    );
    return (
        <section className="receipt-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: title || "签收" }]} />
            {isSupplyChain ? (
                <ZhInsetTabs
                    active={active}
                    onChange={setActive}
                    options={[
                        { label: "我的签收单", value: "Down" },
                        { label: "供应商签收单", value: "Up" }
                    ]}
                />
            ) : null}
            <div className="receipt-list-table">
                <ProTable
                    key={`${active}-${refreshKey}`}
                    title={title}
                    rowKey={row => row.purchaseGoodsReceipt.id}
                    columns={columns}
                    initialParams={{ queryType }}
                    request={params => purchaseGoodsReceiptService.paging({ ...params, queryType })}
                    header={
                        !isUp && !isSupplyChain && hasPermission
                            ? () => (
                                  <Button type="primary" onClick={() => navigate("/console/purchaseGoodsReceipt/add")}>
                                      新增
                                  </Button>
                              )
                            : undefined
                    }
                />
            </div>
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await confirmDialog.action();
                    setConfirmDialog(undefined);
                    reload();
                }}
            />
            <ZhMessageBox
                open={Boolean(rejectRow)}
                title="驳回"
                tip={
                    <div className="receipt-reject-box">
                        <p>是否驳回，驳回后该签收单将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejectRow(undefined)}
                onSubmit={async () => {
                    if (!rejectRow || !rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await purchaseGoodsReceiptService.reject({
                        id: rejectRow.purchaseGoodsReceipt.id,
                        reason: rejectReason.trim()
                    });
                    setRejectRow(undefined);
                    reload();
                }}
            />
        </section>
    );
}

interface ReceiptFormValues {
    projectId: number;
    receiptConfirmedDate: dayjs.Dayjs;
    remark?: string;
}
type ReceiptShipRow = { goods: PurchaseGoods; ship: PurchaseGoods["ships"][number] };
/** 新增与编辑签收单对应旧四列表单、项目摘要、关联弹窗和逐船签收表。 */
export function ReceiptFormPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const forward = params.get("forward");
    const [form] = Form.useForm<ReceiptFormValues>();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const [projects, setProjects] = useState<Project[]>([]);
    const [project, setProject] = useState<any>();
    const [goodsOptions, setGoodsOptions] = useState<PurchaseGoods[]>([]);
    const [selectedGoods, setSelectedGoods] = useState<PurchaseGoods[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [associateOpen, setAssociateOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitValues, setSubmitValues] = useState<ReceiptFormValues>();
    const [sourceReceipt, setSourceReceipt] = useState<PurchaseGoodsReceiptVo>();
    const [receiverCustomerId, setReceiverCustomerId] = useState<number>();
    const [paymentType, setPaymentType] = useState<number>();
    const [changeableShipIds, setChangeableShipIds] = useState<string[]>([]);
    useEffect(() => {
        void projectService.noPaging({ states: String(PROJECT_STATE.PROCESSING.code) } as never).then(setProjects);
    }, []);
    const loadGoods = async (projectId: number) => {
        const list = await purchaseGoodsService.getList(String(projectId));
        list.forEach(item =>
            item.ships.forEach(ship => {
                ship.receiptQuantity ||= ship.ton;
                ship.receiptDate ||= dayjs().format("YYYY-MM-DD");
            })
        );
        setGoodsOptions(list);
        return list;
    };
    useEffect(() => {
        if (!id) return;
        void purchaseGoodsReceiptService
            .getInfo(id)
            .then(async info => {
                setSourceReceipt(info);
                setProject(info.project);
                await loadGoods(info.purchaseGoodsReceipt.projectId);
                const selected = info.purchaseGoodsList.map(item => ({
                    ...item,
                    ships: item.ships.map(ship => ({ ...ship }))
                }));
                setSelectedGoods(selected);
                setSelectedIds(selected.map(item => item.id));
                form.setFieldsValue({
                    projectId: info.purchaseGoodsReceipt.projectId,
                    receiptConfirmedDate: dayjs(info.purchaseGoodsReceipt.receiptConfirmedDate),
                    remark: info.purchaseGoodsReceipt.remark
                });
                const [buyers, rejectedReceipts] = await Promise.all([
                    projectService.getBuyers(info.purchaseGoodsReceipt.projectId),
                    purchaseGoodsReceiptService.getAssociateReceiptsById({
                        relatedReceiptId: info.purchaseGoodsReceipt.id,
                        state: PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code
                    })
                ]);
                setReceiverCustomerId(buyers[0]?.buyerId);
                setPaymentType(
                    isSupplyChain
                        ? PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
                        : PURCHASE_TYPE.DIRECT.match(info.project.purType)
                          ? PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                          : PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code
                );
                setChangeableShipIds(
                    rejectedReceipts.flatMap(receipt =>
                        receipt.purchaseGoodsList.flatMap(goods => goods.ships.map(ship => ship.id))
                    )
                );
            })
            .catch(() => void message.error("加载失败"));
    }, [form, id]);
    const selectProject = async (projectId: number) => {
        const [info, buyers] = await Promise.all([projectService.getInfo(projectId), projectService.getBuyers(projectId)]);
        setProject(info.project);
        setReceiverCustomerId(buyers[0]?.buyerId);
        setPaymentType(
            isSupplyChain
                ? PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
                : PURCHASE_TYPE.DIRECT.match(info.project.purType)
                  ? PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                  : PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code
        );
        setSelectedGoods([]);
        setSelectedIds([]);
        await loadGoods(projectId);
    };
    const rows = selectedGoods.flatMap(goods => goods.ships.map(ship => ({ goods, ship })));
    const updateShip = (row: ReceiptShipRow, patch: Partial<ReceiptShipRow["ship"]>) =>
        setSelectedGoods(items =>
            items.map(item =>
                item.id === row.goods.id
                    ? { ...item, ships: item.ships.map(ship => (ship === row.ship ? { ...ship, ...patch } : ship)) }
                    : item
            )
        );
    const columns: ProColumn<ReceiptShipRow>[] = [
        {
            key: "id",
            title: "提货编号",
            width: 150,
            render: (_, row) => (
                <Button
                    className="receipt-code-link"
                    type="link"
                    onClick={() => navigate(`/console/purchaseGoods/detail?id=${row.goods.id}&purGoodsType=Down`)}
                >
                    {row.goods.id}
                </Button>
            )
        },
        { key: "applyDate", title: "提货申请日期", render: (_, row) => dateFormat(row.goods.applyDate) },
        { key: "ship", title: "船名", render: (_, row) => [row.ship.cnName, row.ship.name].filter(Boolean).join("/") },
        { key: "ton", title: "提货吨数", render: (_, row) => formatNumber(row.ship.ton) },
        {
            key: "receiptQuantity",
            title: "签收吨数",
            render: (_, row) => (
                <InputNumber
                    min={1}
                    max={row.ship.ton}
                    disabled={Boolean(changeableShipIds.length && !changeableShipIds.includes(row.ship.id))}
                    value={row.ship.receiptQuantity}
                    placeholder="请输入签收吨数"
                    onChange={value => updateShip(row, { receiptQuantity: Number(value) })}
                />
            )
        },
        {
            key: "receiptDate",
            title: "签收日期",
            render: (_, row) => (
                <DatePicker
                    value={row.ship.receiptDate ? dayjs(row.ship.receiptDate) : undefined}
                    disabled={Boolean(changeableShipIds.length && !changeableShipIds.includes(row.ship.id))}
                    placeholder="日期选择器"
                    onChange={value => updateShip(row, { receiptDate: value?.format("YYYY-MM-DD") })}
                />
            )
        }
    ];
    /** 逐船签收吨数汇总回提货单条目，保持旧 watch 计算规则。 */
    const submit = (values: ReceiptFormValues) => {
        if (!isSupplyChain && !selectedGoods.length) {
            void message.error("请选择需要关联的提货单");
            return;
        }
        setSubmitValues(values);
    };
    return (
        <section className="receipt-form-page">
            <div className="receipt-form-card common-card">
                <Breadcrumb
                    routes={[
                        { name: "采购" },
                        { name: "签收", path: "/console/purchaseGoodsReceipt" },
                        { name: forward ? "转发签收单" : id ? "修改签收单" : "新增签收单" }
                    ]}
                />
                <h1 className="common-title">{forward ? "转发签收单" : id ? "修改签收单" : "新增签收单"}</h1>
                <Form form={form} layout="vertical" onFinish={submit}>
                    <div className="receipt-form-grid">
                        <div className="receipt-project-field">
                            <Form.Item
                                name="projectId"
                                label="关联项目"
                                rules={[{ required: true, message: "请选择需要关联的项目" }]}
                            >
                                <Select
                                    showSearch
                                    filterOption={false}
                                    disabled={Boolean(id)}
                                    placeholder="请下拉选择或搜索项目"
                                    options={projects.map(item => ({ label: item.name, value: item.id }))}
                                    onSearch={value =>
                                        void projectService
                                            .noPaging({
                                                states: String(PROJECT_STATE.PROCESSING.code),
                                                name: value
                                            } as never)
                                            .then(setProjects)
                                    }
                                    onChange={selectProject}
                                />
                            </Form.Item>
                            {project ? (
                                <div className="receipt-project-summary">
                                    <span>
                                        供货单位<strong>{project.supplierEnterprise?.name || "--"}</strong>
                                    </span>
                                    <span>
                                        签收单位
                                        <strong>
                                            {project.supplyChainEnterprise?.name ||
                                                project.buyerEnterprise?.name ||
                                                "--"}
                                        </strong>
                                    </span>
                                    <span>
                                        品类
                                        <strong>
                                            {[project.productTypeName, project.area, project.productTypeId]
                                                .filter(Boolean)
                                                .join(" | ") || "--"}
                                        </strong>
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        {!isSupplyChain ? (
                            <div className="receipt-associate">
                                <label>关联提货单</label>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        if (!form.getFieldValue("projectId")) {
                                            void message.info("请先关联项目");
                                            return;
                                        }
                                        setAssociateOpen(true);
                                    }}
                                >
                                    {selectedGoods.length ? "重新关联" : "关联"}
                                    <i className="iconfont icon-Frame-3" />
                                </Button>
                            </div>
                        ) : (
                            <div className="receipt-forward-title">供应商签收单</div>
                        )}
                        <div className="receipt-goods-table">
                            {selectedGoods.length ? (
                                <ProTable
                                    data={rows}
                                    pagination={false}
                                    rowKey={row => `${row.goods.id}-${row.ship.id}-${row.ship.orderShipId || ""}`}
                                    columns={columns}
                                />
                            ) : null}
                        </div>
                        {isSupplier ? (
                            <>
                                <Form.Item
                                    name="receiptConfirmedDate"
                                    label="确认日期"
                                    rules={[{ required: true, message: "请选择签收确认日期" }]}
                                >
                                    <DatePicker
                                        disabled={Boolean(changeableShipIds.length)}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                                <Form.Item className="receipt-remark" name="remark" label="备注">
                                    <Input.TextArea
                                        disabled={Boolean(changeableShipIds.length)}
                                        maxLength={200}
                                        showCount
                                        placeholder="如有其它说明, 请备注"
                                    />
                                </Form.Item>
                            </>
                        ) : null}
                    </div>
                </Form>
            </div>
            {isSupplyChain ? (
                <div className="receipt-form-card common-card">
                    <div className="receipt-forward-title">关联下游提货单</div>
                    {sourceReceipt?.relatedPurchaseGoodsList?.length ? (
                        <ProTable
                            data={sourceReceipt.relatedPurchaseGoodsList.flatMap(goods =>
                                goods.ships.map(ship => ({ goods, ship }))
                            )}
                            pagination={false}
                            rowKey={row => `${row.goods.id}-${row.ship.id}`}
                            columns={columns.slice(0, 4)}
                        />
                    ) : null}
                </div>
            ) : null}
            <div className="detail-buttons receipt-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    保存为草稿
                </Button>
                <Button type="primary" onClick={() => navigate("/console/purchaseGoodsReceipt")}>返回</Button>
            </div>
            <Modal
                open={associateOpen}
                width={582}
                title="关联供应商提货单"
                onCancel={() => setAssociateOpen(false)}
                onOk={() => {
                    setSelectedGoods(
                        goodsOptions
                            .filter(item => selectedIds.includes(item.id))
                            .map(item => ({ ...item, ships: item.ships.map(ship => ({ ...ship })) }))
                    );
                    setAssociateOpen(false);
                }}
            >
                <Checkbox.Group value={selectedIds} onChange={ids => setSelectedIds(ids as string[])}>
                    <div className="receipt-associate-options">
                        {goodsOptions.map(item => (
                            <Checkbox value={item.id} key={item.id}>
                                <strong>{item.id}</strong>
                                <span>
                                    船名{" "}
                                    {item.ships
                                        .map(ship => [ship.cnName, ship.name].filter(Boolean).join("/"))
                                        .join("、")}
                                </span>
                                <span>吨数 {formatNumber(item.quantity)}</span>
                                <span>申请日期 {dateFormat(item.applyDate)}</span>
                            </Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
            </Modal>
            <ZhMessageBox
                open={Boolean(submitValues)}
                tip="是否保存为草稿，保存后您需签署后才能提交"
                onCancel={() => setSubmitValues(undefined)}
                onSubmit={async () => {
                    if (!submitValues) return;
                    setSubmitting(true);
                    const purchaseGoodsList = selectedGoods.map(item => ({
                        id: item.id,
                        arrayGoodsShip: item.ships,
                        receiptQuantity: String(
                            item.ships.reduce((sum, ship) => sum + Number(ship.receiptQuantity || 0), 0)
                        )
                    }));
                    const payload = {
                        projectId: submitValues.projectId,
                        receiptConfirmedDate: submitValues.receiptConfirmedDate.format("YYYY-MM-DD HH:mm:ss"),
                        remark: submitValues.remark,
                        receiverCustomerId,
                        type: paymentType,
                        quantity: isSupplyChain
                            ? sourceReceipt?.purchaseGoodsReceipt.quantity
                            : purchaseGoodsList.reduce((sum, item) => sum + Number(item.receiptQuantity), 0),
                        ...(isSupplyChain
                            ? { relatedReceiptId: sourceReceipt?.purchaseGoodsReceipt.id }
                            : { purchaseGoodsList })
                    } as PurchaseGoodsReceiptForm;
                    try {
                        if (id && !forward) await purchaseGoodsReceiptService.update(id, payload);
                        else await purchaseGoodsReceiptService.create(payload);
                        setSubmitValues(undefined);
                        navigate("/console/purchaseGoodsReceipt");
                    } finally {
                        setSubmitting(false);
                    }
                }}
            />
        </section>
    );
}

/** 签收详情展示关联提货单和下游签收单，保留项目与对账跳转。 */
export function ReceiptDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const [info, setInfo] = useState<PurchaseGoodsReceiptVo>();
    const [confirmDialog, setConfirmDialog] = useState<{
        title?: string;
        tip: string;
        action: () => Promise<unknown>;
    }>();
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const load = async () => {
        if (!id) return;
        try {
            setInfo(await purchaseGoodsReceiptService.getInfo(id));
        } catch {
            void message.error("加载失败");
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    if (!info) return null;
    const receipt = info.purchaseGoodsReceipt;
    const active: ReceiptActive = params.get("purGoodsReceiptType") === "Up" ? "Up" : "Down";
    const isUp = active === "Up";
    const user = useUserSession.getState();
    const account = useAccountSession.getState();
    const roleCode = user.roleCode ?? ROLE_CODE.CUSTOMER.code;
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT ||
        Boolean((user.userInfo as any).permissions?.includes(PERMISSION.SIGNATURE.code));
    const enterpriseName = (value: any) => value?.name || value?.legalRepresentative || value?.realName || "--";
    const productType = [
        info.project?.productTypeName,
        (info.project as any)?.area,
        (info.project as any)?.productTypeId
    ]
        .filter(Boolean)
        .join(" | ");
    const title = isUp && !isCustomer ? "供应商签收单详情" : "我的签收单详情";
    const goodsRows: ReceiptShipRow[] = info.purchaseGoodsList.flatMap(goods =>
        goods.ships.map(ship => ({ goods, ship }))
    );
    const goodsColumns: ProColumn<ReceiptShipRow>[] = [
        {
            key: "id",
            title: "提货编号",
            width: 150,
            render: (_, row) => (
                <Button
                    className="receipt-code-link"
                    type="link"
                    onClick={() =>
                        navigate(
                            `/console/purchaseGoods/detail?id=${row.goods.id}&purGoodsType=${isUp ? "My" : "Down"}`
                        )
                    }
                >
                    {row.goods.id}
                </Button>
            )
        },
        { key: "applyDate", title: "提货申请日期", render: (_, row) => dateFormat(row.goods.applyDate) },
        { key: "ship", title: "船名", render: (_, row) => [row.ship.cnName, row.ship.name].filter(Boolean).join("/") },
        { key: "ton", title: "提货吨数", render: (_, row) => formatNumber(row.ship.ton) },
        { key: "receiptQuantity", title: "签收吨数", render: (_, row) => formatNumber(row.ship.receiptQuantity) },
        { key: "receiptDate", title: "签收日期", render: (_, row) => dateFormat(row.ship.receiptDate) }
    ];
    const relatedColumns: ProColumn<any>[] = [
        { key: "id", title: "签收编号", width: 150 },
        { key: "receiver", title: "签收单位", render: (_, row) => enterpriseName(row.receiverEnterprise) },
        { key: "quantity", title: "签收吨数", render: (_, row) => formatNumber(row.quantity) },
        { key: "state", title: "签收状态", render: (_, row) => PURCHASE_GOODS_RECEIPT_STATE.getIntroByCode(row.state) },
        { key: "date", title: "签收日期", render: (_, row) => dateFormat(row.receiptConfirmedDate) }
    ];
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (!data?.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
            return;
        }
        window.open(data.ticket);
        await load();
    };
    const confirm = (tip: string, action: () => Promise<unknown>, title?: string) =>
        setConfirmDialog({ title, tip, action });
    const signState = Number((receipt as any).complexState) & 0xffff;
    const signedSelf =
        signState === SIGN_STATE.ALL_SIGN.code ||
        (isCustomer
            ? signState === SIGN_STATE.BUYER_SIGN.code
            : isSupplier
              ? signState === SIGN_STATE.SELLER_SIGN.code
              : isUp
                ? signState === SIGN_STATE.BUYER_SIGN.code
                : signState === SIGN_STATE.SELLER_SIGN.code);
    const proxyIsReceiver = String(account.proxyActualAccount?.id) === String(receipt.receiverCustomerId);
    const sealUser =
        String(account.actualAccount?.id) ===
        String(
            proxyIsReceiver ? (receipt.receiverEnterprise as any)?.signId : (receipt.supplierEnterprise as any)?.signId
        );
    const related = info.relatedPurchaseGoodsReceiptList || [];
    const relatedRejectable =
        related.length > 0 &&
        related.every(item =>
            [PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code, PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code].includes(
                item.state
            )
        ) &&
        related.some(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code);
    const relatedInvalid =
        related.length > 0 && related.every(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.INVALID.code);
    const invalidSignState = Number(receipt.invalidSignState);
    const invalidSignedSelf =
        invalidSignState === SIGN_STATE.ALL_SIGN.code ||
        (isCustomer
            ? invalidSignState === SIGN_STATE.BUYER_SIGN.code
            : isSupplier
              ? invalidSignState === SIGN_STATE.SELLER_SIGN.code
              : isUp
                ? invalidSignState === SIGN_STATE.BUYER_SIGN.code
                : invalidSignState === SIGN_STATE.SELLER_SIGN.code);
    const actions: DetailAction<PurchaseGoodsReceiptVo>[] = [
        {
            label: "修改",
            visible: () =>
                !isUp &&
                isSupplier &&
                hasPermission &&
                [PURCHASE_GOODS_RECEIPT_STATE.DRAFT.code, PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code].includes(
                    receipt.state
                ),
            action: () => navigate(`/console/purchaseGoodsReceipt/edit?id=${receipt.id}`)
        },
        {
            label: "发起签署",
            visible: () => !isUp && hasPermission && receipt.state === PURCHASE_GOODS_RECEIPT_STATE.DRAFT.code,
            action: () => openTicket(() => purchaseGoodsReceiptService.startSign(receipt.id))
        },
        {
            label: "签署",
            visible: () =>
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.CONFIRMING.code &&
                !signedSelf &&
                sealUser &&
                (!isUp ||
                    isCustomer ||
                    (isSupplyChain &&
                        related.length > 0 &&
                        related.every(item => item.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code))),
            action: () => openTicket(() => purchaseGoodsReceiptService.sign(receipt.id))
        },
        {
            label: "删除",
            visible: () =>
                !isUp &&
                isSupplier &&
                hasPermission &&
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.DRAFT.code &&
                !related.length,
            action: () =>
                confirm("是否删除", async () => {
                    await purchaseGoodsReceiptService.delete(receipt.id);
                    navigate("/console/purchaseGoodsReceipt");
                })
        },
        {
            label: "驳回",
            visible: () =>
                isUp &&
                hasPermission &&
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.CONFIRMING.code &&
                !signedSelf &&
                (isCustomer || (isSupplyChain && relatedRejectable)),
            action: () => {
                setRejectReason("");
                setRejecting(true);
            }
        },
        {
            label: "转发给下游",
            visible: () =>
                isUp &&
                isSupplyChain &&
                receipt.forward === FORWARD.UNFORWARD.code &&
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.CONFIRMING.code,
            action: () => navigate(`/console/purchaseGoodsReceipt/edit?id=${receipt.id}&forward=1`)
        },
        {
            label: "发起作废",
            visible: () =>
                !isUp &&
                isSupplier &&
                hasPermission &&
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code &&
                receipt.reconciliationState === RECIEPT_RECONCILIATION_STATE.INITIAL.code,
            action: () =>
                confirm(
                    "作废后，需双方签署作废签收单后才作废，是否发起作废？",
                    () => openTicket(() => purchaseGoodsReceiptService.startInvalid(Number(receipt.id))),
                    "已发起作废"
                )
        },
        {
            label: "签署作废文件",
            visible: () =>
                receipt.state === PURCHASE_GOODS_RECEIPT_STATE.INVALIDING.code &&
                !invalidSignedSelf &&
                sealUser &&
                (!(isSupplyChain && isUp) || relatedInvalid),
            action: () => openTicket(() => purchaseGoodsReceiptService.sign(receipt.id))
        },
        { label: "返回", action: () => navigate("/console/purchaseGoodsReceipt") }
    ];
    return (
        <section className="receipt-detail-page detail-container">
            <DetailHeader
                title={title}
                leftInfos={[{ label: "编号", value: receipt.id }]}
                rightInfos={[{ label: "更新时间", value: dateTimeFormat(receipt.updatedTime) }]}
                state={{ text: PURCHASE_GOODS_RECEIPT_STATE.getIntroByCode(receipt.state) ?? undefined }}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "签收", path: "/console/purchaseGoodsReceipt" },
                            { name: title }
                        ]}
                    />
                }
            />
            <InfoCard title="基本信息">
                <ZhGrid cols={5} gap={16}>
                    <ZhBaseItem label="项目" span={2}>
                        <Button
                            className="receipt-code-link"
                            type="link"
                            onClick={() => navigate(`/console/project/detail?id=${receipt.projectId}`)}
                        >
                            {receipt.projectName || "--"}
                        </Button>
                    </ZhBaseItem>
                    <ZhBaseItem label="品类" span={2} value={productType || receipt.productTypeName || "--"} />
                    <ZhBaseItem label="签收单位" value={enterpriseName(receipt.receiverEnterprise)} />
                    <ZhBaseItem label="供货单位" value={receipt.supplierEnterprise?.name || "--"} />
                    <ZhBaseItem label="吨数" value={formatNumber(receipt.quantity)} />
                    <ZhBaseItem label="确认日期" span={3} value={dateFormat(receipt.receiptConfirmedDate)} />
                    <ZhBaseItem label="单据" fileId={info.file?.id} />
                    <ZhBaseItem
                        label="对账状态"
                        span={4}
                        value={
                            receipt.state === PURCHASE_GOODS_RECEIPT_STATE.FINISHED.code
                                ? RECIEPT_RECONCILIATION_STATE.getIntroByCode(receipt.reconciliationState)
                                : "--"
                        }
                    />
                    {receipt.reconciliationState === RECIEPT_RECONCILIATION_STATE.COMPLETED.code ? (
                        <ZhBaseItem label="关联对账单">
                            <Button
                                className="receipt-code-link"
                                type="link"
                                onClick={() =>
                                    navigate(`/console/reconciliation/detail?id=${receipt.reconciliationId}`)
                                }
                            >
                                {receipt.reconciliationId || "--"}
                            </Button>
                        </ZhBaseItem>
                    ) : null}
                    <ZhBaseItem label="备注" span={5} value={receipt.remark || "--"} />
                    {receipt.state === PURCHASE_GOODS_RECEIPT_STATE.REJECTED.code ? (
                        <ZhBaseItem label="驳回理由" span={5} value={receipt.reason || "--"} />
                    ) : null}
                </ZhGrid>
            </InfoCard>
            {info.purchaseGoodsList ? (
                <InfoCard title="提货单">
                    <ProTable
                        data={goodsRows}
                        pagination={false}
                        rowKey={row => `${row.goods.id}-${row.ship.id}-${row.ship.orderShipId || ""}`}
                        columns={goodsColumns}
                    />
                </InfoCard>
            ) : null}
            {isSupplyChain && isUp ? (
                <InfoCard title="下游签收单">
                    <ProTable data={related} pagination={false} rowKey="id" columns={relatedColumns} />
                </InfoCard>
            ) : null}
            {receipt.state === PURCHASE_GOODS_RECEIPT_STATE.INVALIDING.code ||
            receipt.state === PURCHASE_GOODS_RECEIPT_STATE.INVALID.code ? (
                <InfoCard title="作废信息">
                    <ZhGrid cols={5} gap={16}>
                        <ZhBaseItem label="文件" fileId={receipt.invalidFileId} />
                        {receipt.state === PURCHASE_GOODS_RECEIPT_STATE.INVALID.code ? (
                            <ZhBaseItem label="时间" value={dateTimeFormat(receipt.invalidTime)} />
                        ) : null}
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <DetailButtons actions={actions} entity={info} />
            <ZhMessageBox
                open={Boolean(confirmDialog)}
                title={confirmDialog?.title}
                tip={confirmDialog?.tip}
                onCancel={() => setConfirmDialog(undefined)}
                onSubmit={async () => {
                    if (!confirmDialog) return;
                    await confirmDialog.action();
                    setConfirmDialog(undefined);
                    await load();
                }}
            />
            <ZhMessageBox
                open={rejecting}
                title="驳回"
                tip={
                    <div className="receipt-reject-box">
                        <p>是否驳回，驳回后该签收单将不再展示</p>
                        <Input.TextArea
                            value={rejectReason}
                            placeholder="请输入驳回理由"
                            onChange={event => setRejectReason(event.target.value)}
                        />
                    </div>
                }
                onCancel={() => setRejecting(false)}
                onSubmit={async () => {
                    if (!rejectReason.trim()) {
                        void message.error("请输入驳回理由");
                        return;
                    }
                    await purchaseGoodsReceiptService.reject({ id: receipt.id, reason: rejectReason.trim() });
                    setRejecting(false);
                    await load();
                }}
            />
        </section>
    );
}
